import React, { useMemo, useState } from "react";
import { Button, Card, Table, Dropdown } from "react-bootstrap";
import {
  ArrowLeft,
  ChevronDown,
  Download,
  InfoCircle,
  PencilFill,
  TrophyFill,
} from "react-bootstrap-icons";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import { useNavigate } from "react-router-dom";

const dummyCategories = [
  { name: "Coolup", orders: 19, revenue: 4357.15 },
  { name: "Sandwich", orders: 36, revenue: 2938.55 },
  { name: "Freezio", orders: 244, revenue: 2876.17 },
  { name: "Hot Classic Classic Classic", orders: 30, revenue: 2821.85 },
  { name: "Pizzap", orders: 31, revenue: 1221 },
  { name: "Extras", orders: 8, revenue: 819.05 },
  { name: "Wraps", orders: 5, revenue: 619.05 },
  { name: "Calzones", orders: 9, revenue: 438.1 },
];

const CategoriesInsightDetails = () => {
  const navigate = useNavigate();
  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [, setSkin] = useState(currentSkin);

  const [metric, setMetric] = useState("revenue");
  const [selectedRange, setSelectedRange] = useState("Last 12 Months");
  const [showAll, setShowAll] = useState(false);

  const processedData = useMemo(() => {
    const totalOrders = dummyCategories.reduce((sum, item) => sum + item.orders, 0);
    const totalRevenue = dummyCategories.reduce((sum, item) => sum + item.revenue, 0);

    return [...dummyCategories]
      .sort((a, b) => b[metric] - a[metric])
      .map((item, index) => {
        const baseTotal = metric === "revenue" ? totalRevenue : totalOrders;
        const value = metric === "revenue" ? item.revenue : item.orders;

        return {
          ...item,
          rank: index + 1,
          percentage: baseTotal ? (value / baseTotal) * 100 : 0,
        };
      });
  }, [metric]);

  const topData = showAll ? processedData.slice(0, 10) : processedData.slice(0, 5);
  const winner = processedData[0];
  const maxValue = Math.max(...processedData.map((item) => item[metric]));

  const formatCurrency = (amount) =>
    `₹${Number(amount).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
      minimumFractionDigits: Number(amount) % 1 === 0 ? 0 : 2,
    })}`;

  const getMetricText = (item) => {
    if (metric === "revenue") {
      return `${formatCurrency(item.revenue)} (${item.percentage.toFixed(1)}%) revenue`;
    }

    return `${item.orders} (${item.percentage.toFixed(1)}%) orders`;
  };

  return (
    <>
      <Header onSkin={setSkin} />

      <div className="main main-app">
        <div className="best-selling-page">
          <div className="top-row">
            <Button variant="light" onClick={() => navigate("/dashboard")} className="go-back-btn">
              <ArrowLeft size={20} />
              Go Back
            </Button>

            <Button variant="light" className="export-btn">
              <Download size={18} />
              Export
            </Button>
          </div>

          <h1 className="page-title">
            What are the <span>best-selling categories</span> of my brand?
          </h1>

          <div className="filter-panel">
            <div className="tab-buttons">
              <button onClick={() => navigate("/smart-insights/items-insight-details")}>Items</button>
              <button className="active">Categories</button>
            </div>

            <div className="filter-actions">
              <div className="metric-tabs">
                <button className={metric === "revenue" ? "active" : ""} onClick={() => setMetric("revenue")}>
                  Revenue
                </button>

                <button className={metric === "orders" ? "active" : ""} onClick={() => setMetric("orders")}>
                  Orders
                </button>
              </div>

              <Dropdown>
                <Dropdown.Toggle className="range-btn">
                  {selectedRange}
                  <ChevronDown size={18} />
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  {["Last 7 Days", "Last 30 Days", "Last 6 Months", "Last 12 Months"].map((range) => (
                    <Dropdown.Item key={range} onClick={() => setSelectedRange(range)}>
                      {range}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>

          <Card className="chart-card">
            <Card.Body>
              <div className="card-head">
                <div>
                  <h2>{winner?.name}</h2>
                  <p>Generated the most {metric} in {selectedRange.toLowerCase()}</p>
                </div>
              </div>

              <div className="bar-chart">
                {topData.map((item, index) => {
                  const width = maxValue ? (item[metric] / maxValue) * 100 : 0;
                  const showTextInside = width > Math.max(35, item.name.length * 1.5);

                  return (
                    <div className="bar-row" key={item.name}>
                      <div className="bar-container">
                        <div
                          className="bar-fill"
                          style={{
                            width: `${width}%`,
                            background:
                              index === 0
                                ? "#5b24c9"
                                : "linear-gradient(90deg, #8067f1, #5b24c9)",
                          }}
                        >
                          {showTextInside && (
                            <div className="bar-content bar-content-inside">
                              <div className="bar-name">
                                {item.rank}. {item.name}
                                {index === 0 && <TrophyFill className="crown" />}
                              </div>
                              <div className="bar-detail">{getMetricText(item)}</div>
                            </div>
                          )}
                        </div>

                        {!showTextInside && (
                          <div
                            className="bar-content bar-content-outside"
                            style={{
                              left: `calc(${width}% + 12px)`,
                            }}
                          >
                            <div className="bar-name">
                              {item.rank}. {item.name}
                              {index === 0 && <TrophyFill className="crown" />}
                            </div>
                            <div className="bar-detail">{getMetricText(item)}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {processedData.length > 5 && (
                <button className="show-more-btn" onClick={() => setShowAll(!showAll)}>
                  {showAll ? "Show less" : "Show more"}
                  <ChevronDown
                    size={18}
                    style={{
                      transform: showAll ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  />
                </button>
              )}
            </Card.Body>
          </Card>

          <div className="table-wrap">
            <Table borderless responsive className="insight-table">
              <thead>
                <tr>
                  <th>Category Name</th>
                  <th>Total Orders (%) ↕</th>
                  <th>Revenue Generated ↕</th>
                </tr>
              </thead>

              <tbody>
                {processedData.map((item) => {
                  const totalOrders = processedData.reduce((sum, row) => sum + row.orders, 0);
                  const orderPercent = totalOrders ? (item.orders / totalOrders) * 100 : 0;

                  return (
                    <tr key={item.name}>
                      <td>{item.name}</td>
                      <td>
                        {item.orders} ({orderPercent.toFixed(1)}%)
                      </td>
                      <td>{formatCurrency(item.revenue)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </div>
      </div>

      <Footer />

      <InsightStyles />
    </>
  );
};

const InsightStyles = () => (
  <style>
    {`
      .best-selling-page {
        background: #ffffff;
        min-height: 100vh;
        padding: 28px;
      }

      .top-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 22px;
      }

      .go-back-btn,
      .export-btn {
        border: 1px solid #d7e0e2 !important;
        background: #ffffff !important;
        color: #071c1f !important;
        border-radius: 13px !important;
        font-weight: 700 !important;
        display: inline-flex !important;
        align-items: center;
        gap: 9px;
      }

      .page-title {
        font-size: 35px;
        font-weight: 800;
        color: #071c1f;
        margin-bottom: 28px;
      }

      .page-title span {
        color: #3498db;
      }

      .filter-panel {
        background: #f7f8f8;
        border-radius: 15px;
        padding: 14px 20px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 20px;
        margin-bottom: 24px;
      }

      .tab-buttons,
      .metric-tabs {
        display: flex;
        gap: 8px;
        align-items: center;
      }

      .tab-buttons button,
      .metric-tabs button {
        border: 1px solid #d7e0e2;
        background: #ffffff;
        color: #071c1f;
        border-radius: 9px;
        padding: 11px 17px;
        font-size: 17px;
        font-weight: 800;
      }

      .tab-buttons button.active {
        background: #3498db;
        border-color: #3498db;
        color: #ffffff;
      }

      .metric-tabs button {
        border-color: transparent;
        background: transparent;
      }

      .metric-tabs button.active {
        background: #ffffff;
        border-color: #d7e0e2;
      }

      .filter-actions {
        display: flex;
        align-items: center;
        gap: 22px;
      }

      .range-btn {
        min-width: 235px;
        border: 1px solid #d7e0e2 !important;
        background: #ffffff !important;
        color: #27383b !important;
        border-radius: 10px !important;
        padding: 12px 16px !important;
        font-size: 16px !important;
        font-weight: 600 !important;
        display: flex !important;
        align-items: center;
        justify-content: space-between;
      }

      .range-btn::after {
        display: none;
      }

      .chart-card {
        border: 1px solid #dce5e7 !important;
        border-radius: 17px !important;
      }

      .chart-card .card-body {
        padding: 30px !important;
      }

      .card-head {
        margin-bottom: 21px;
      }

      .card-head h2 {
        font-size: 42px;
        font-weight: 800;
        color: #050f10;
        margin-bottom: 12px;
      }

      .card-head p {
        font-size: 18px;
        color: #17292b;
        margin: 0;
      }

      .bar-chart {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .bar-row {
        min-height: 58px;
      }

      .bar-container {
        position: relative;
        width: 100%;
        min-height: 58px;
      }

      .bar-fill {
        height: 58px;
        min-width: 26px;
        border-radius: 7px;
        display: flex;
        align-items: center;
        padding: 0 14px;
        color: #ffffff;
        overflow: hidden;
      }

      .bar-content {
        display: flex;
        align-items: center;
        gap: 14px;
        white-space: nowrap;
      }

      .bar-content-inside {
        color: #ffffff;
      }

      .bar-content-outside {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        color: #9d9d9d;
        pointer-events: none;
      }

      .bar-name {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        font-weight: 900;
      }

      .bar-detail {
        font-size: 14px;
        font-weight: 800;
        opacity: 0.95;
      }

      .bar-content-outside .bar-detail {
        color: #344244;
      }

      .crown {
        color: #ffd429;
        flex-shrink: 0;
      }

      .show-more-btn {
        margin-top: 17px;
        background: transparent;
        border: none;
        color: #071c1f;
        font-size: 16px;
        font-weight: 800;
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 0;
      }

      .table-wrap {
        margin-top: 42px;
      }

      .insight-table {
        border-collapse: separate !important;
        border-spacing: 0 16px !important;
      }

      .insight-table thead th {
        font-size: 17px;
        color: #344244;
        font-weight: 800;
        padding: 0 22px 6px !important;
        border: none !important;
      }

      .insight-table tbody tr {
        background: #f6f8f8;
      }

      .insight-table tbody td {
        padding: 17px 22px !important;
        font-size: 16px;
        font-weight: 600;
        color: #344244;
        border: none !important;
      }

      .insight-table tbody td:first-child {
        border-radius: 13px 0 0 13px;
      }

      .insight-table tbody td:last-child {
        border-radius: 0 13px 13px 0;
      }

      @media (max-width: 991px) {
        .filter-panel,
        .card-head {
          flex-direction: column;
          align-items: stretch;
        }

        .filter-actions {
          flex-direction: column;
          align-items: stretch;
        }

        .range-btn {
          width: 100%;
        }

        .bar-content {
          flex-direction: column;
          align-items: flex-start;
          gap: 2px;
        }

        .bar-content-outside {
          max-width: calc(100% - 50px);
        }

        .bar-name,
        .bar-detail {
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    `}
  </style>
);

export default CategoriesInsightDetails;